import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { MagnifyingGlassIcon, PrinterIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { add, addYears, differenceInDays, format, intervalToDuration, sub, subYears } from "date-fns";
import _ from "lodash";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import { Actions as LEAVEACTIONS } from "../../store/actions/leaveActions";
import { DateInputFormat } from "../common/DateComponents";
import Spinner from "../common/Spinner";
import LeaveRow from "./LeaveRow";
import { employmentTypeOptions } from "../company-settings/config/employmentTypeOptions";
import { toTitleCase } from "@/common/validation/Validators";

const LeaveTable = ({
	leaveUpdating,
	leave,
	departmentOptions,
	getLeave,
	employeeOptions,
	leaveTypeOptions,
	leaveStatusOptions,
	saveLeave,
	showCancelLeave,
	jobTypeOptions,
	detailedReportDownloading,
	downloadDetailedReport,
	hasDepartments,
	jurisdiction,
	location,
	employeeId,
}) => {
	const [employee, setEmployee] = useState((employeeId.value && employeeId) || null);
	const [selectedDepartment, setSelectedDepartment] = useState();
	const [leaveType, setLeaveType] = useState();
	const [jobType, setJobType] = useState();
	const [leaveStatus, setLeaveStatus] = useState(
		location == "leavePage" && {
			value: "Pending",
			label: "Pending",
		},
	);
	// const today = set(new Date(), { month: 4, date: 6, year: 2023 });
	const today = new Date();

	const [startDate, setStartDate] = useState(() => {
		let firstDayOfMonth;
		switch (jurisdiction) {
			case "UK":
				let yearToSubtract = 1;
				if (today.getMonth() >= 3 && today.getDate() > 5) yearToSubtract = 0;
				firstDayOfMonth = new Date(today.getFullYear() - yearToSubtract, 3, 6);
				break;
			case "MT":
			default:
				firstDayOfMonth = new Date(today.getFullYear(), 0, 1);
				break;
		}

		return new Date(firstDayOfMonth.getTime() - firstDayOfMonth.getTimezoneOffset() * 60000);
	});
	const [endDate, setEndDate] = useState(() => {
		let lastDayOfMonth;
		switch (jurisdiction) {
			case "UK":
				let yearToAdd = 0;
				if (today.getMonth() >= 3 && today.getDate() > 5) yearToAdd = 1;
				lastDayOfMonth = new Date(today.getFullYear() + yearToAdd, 3, 5);
				break;
			case "MT":
			default:
				lastDayOfMonth = new Date(today.getFullYear(), 12, 0);
				break;
		}

		return new Date(lastDayOfMonth.getTime() - lastDayOfMonth.getTimezoneOffset() * 60000);
	});

	const updateLeave = () => {
		const from =
			startDate &&
			new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().substring(0, 10);
		const to =
			endDate && new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().substring(0, 10);

		getLeave(
			cleanDeep({
				from,
				to,
				employee: employee?.value,
				type: leaveType?.value,
				status: leaveStatus?.value,
				department: selectedDepartment?.value,
			}),
		);
	};

	useEffect(() => {
		if (leaveUpdating || location == "leavePage") return;
		leaveUpdating = true;
		updateLeave();
	}, [employee, selectedDepartment, leaveType, leaveStatus, jobType, startDate, endDate]);

	const getDetailedLeaveReport = () => {
		const from =
			startDate &&
			new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().substring(0, 10);
		const to =
			endDate && new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().substring(0, 10);

		downloadDetailedReport(
			cleanDeep({
				from,
				to,
				employee: employee && employee.value,
				type: leaveType && leaveType.value,
				status: leaveStatus && leaveStatus.value,
				jobType: jobType && jobType.value,
				department: selectedDepartment && selectedDepartment.value,
			}),
		);
	};

	const urlParams = new URLSearchParams(window.location.search);
	const urlStatus = urlParams.get("status");
	const urlleaveID = urlParams.get("leaveID");

	useEffect(() => {
		if (urlStatus && urlleaveID && leave.length) {
			let leaveItem = leave.find(({ id }) => id == urlleaveID);

			if (leaveItem && urlStatus) {
				if (urlStatus == "Approved" || urlStatus == "Cancelled") {
					leaveItem.status = urlStatus;
					saveLeave(leaveItem);
					urlParams.delete("status");
					urlParams.delete("leaveID");
					window.history.pushState(
						null,
						null,
						`${window.location.pathname}${urlParams.toString() ? "?" : ""}${urlParams.toString()}`,
					);
				}
				if (urlStatus == "Denied") {
					showCancelLeave(leaveItem, "Denied");
				} else {
					NotificationManager.error("Invalid Leave Status", "Error");
					urlParams.delete("status");
					urlParams.delete("leaveID");
					window.history.pushState(
						null,
						null,
						`${window.location.pathname}${urlParams.toString() ? "?" : ""}${urlParams.toString()}`,
					);
				}
			}
		}
	}, [urlleaveID, urlStatus, leave.length]);

	// this mutates object not ideal
	if (jobType && leave && leave[0] && !leave[0].jobType) {
		leave.map((item) => {
			const option = employeeOptions.find((employee) => employee.value == item.agreement);
			if (option) {
				item.jobType = option.jobType;
			} else {
				console.log("noEmployee??", item.agreement);
			}
		});
	}

	return (
		<div className="content">
			<div className="form-group">
				<div>
					<div className="col-sm-3 col-xs-4">
						<label>From</label>
						<DatePicker
							selected={startDate}
							wrapperClassName="full-width"
							onChange={(date) => {
								if (!date) return;
								if (differenceInDays(endDate, date) <= 0) {
									const interval = intervalToDuration({
										start: startDate,
										end: endDate,
									});
									const newEndDate = add(date, interval);
									setEndDate(newEndDate);
								}
								setStartDate(date);
							}}
							showYearDropdown
							minDate={subYears(new Date(), 100)}
							maxDate={addYears(new Date(), 20)}
							dateFormat="dd/MM/yyyy"
							customInput={<DateInputFormat />}
						/>
						<div className="clearfix" />
					</div>
					<div className="col-sm-3 col-xs-4">
						<label htmlFor=" ">To</label>
						<DatePicker
							wrapperClassName="full-width"
							selected={endDate}
							onChange={(date) => {
								if (!date) return;
								if (differenceInDays(date, startDate) < 0) {
									const interval = intervalToDuration({
										start: endDate,
										end: startDate,
									});
									const newStartDate = sub(date, interval);
									setStartDate(newStartDate);
								}
								setEndDate(date);
							}}
							showYearDropdown
							maxDate={addYears(new Date(), 20)}
							dateFormat="dd/MM/yyyy"
							customInput={<DateInputFormat />}
						/>
						<div className="clearfix" />
					</div>
					<div className="col-sm-3 col-xs-4">
						<label htmlFor="material-text">Status</label>
						<Select
							value={leaveStatus}
							options={leaveStatusOptions}
							onChange={setLeaveStatus}
							isClearable
						/>
						<div className="clearfix" />
					</div>
					<div className="col-sm-3 col-xs-4">
						<label htmlFor="material-text">Type</label>
						<Select
							value={leaveType}
							options={leaveTypeOptions}
							onChange={setLeaveType}
							isClearable
						/>
						<div className="clearfix" />
					</div>
					<div className="clearfix" />
				</div>
				<div
					className="mt-5"
					style={{
						display: `${location == "leavePage" ? "" : "none"}`,
					}}
				>
					<div className="col-sm-3 col-xs-4">
						<label htmlFor="material-text">Employee</label>
						<Select
							value={employee}
							options={employeeOptions}
							onChange={setEmployee}
							isClearable
						/>
						<div className="clearfix" />
					</div>
					{hasDepartments ? (
						<div className="col-sm-3 col-xs-4">
							<label htmlFor="material-text">Department</label>
							<Select
								value={selectedDepartment}
								options={departmentOptions}
								onChange={setSelectedDepartment}
								isClearable
							/>
							<div className="clearfix" />
						</div>
					) : null}
					<div className="col-sm-3 col-xs-4">
						<label htmlFor="material-text">Employment Type</label>
						<Select
							value={jobType}
							options={jobTypeOptions}
							onChange={setJobType}
							isClearable
						/>
						<div className="clearfix" />
					</div>

					<div
						className="col-sm-3 col-xs-4 pull-right"
						style={{ paddingTop: 27 }}
					>
						<button
							type="button"
							className="btn btn-success pull-right"
							style={{ marginLeft: 10 }}
							disabled={leaveUpdating}
							onClick={() => updateLeave()}
						>
							{leaveUpdating ? (
								<span>
									<Spinner
										style={{ height: "1.5em", verticalAlign: "middle", float: "left" }}
										className="animate-spin -ml-1 mr-3 h-5 w-5 "
									/>
									View Leave
								</span>
							) : (
								<span>
									<MagnifyingGlassIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
									View Leave
								</span>
							)}
						</button>
						<button
							type="button"
							className="btn btn-default pull-right"
							disabled={detailedReportDownloading}
							onClick={getDetailedLeaveReport}
						>
							{detailedReportDownloading ? (
								<Spinner
									style={{ height: "1.5em", float: "left" }}
									className="animate-spin -ml-1 mr-3 h-5 w-5 "
								/>
							) : (
								<PrinterIcon style={{ height: "1.5em", float: "left" }} />
							)}
						</button>
						<div className="clearfix" />
					</div>
					<div className="clearfix" />
				</div>
			</div>

			<div className="table-responsive">
				<table
					id="table-timesheets"
					className="js-table-sections table table-hover"
				>
					<thead>
						<tr>
							<th width="5%"></th>
							{location == "leavePage" && <th>Employee</th>}
							<th>Dept.</th>
							<th>Appl. Date</th>
							<th>From</th>
							<th>To</th>
							<th>Hours</th>
							<th>Type</th>
							<th>Status</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{(jobType ? leave.filter((item) => item.jobType == jobType.value) : leave).map((entry) => (
							<LeaveRow
								key={entry.id}
								leave={entry}
								location={location}
							/>
						))}
					</tbody>
				</table>
			</div>

			<div className="clearfix" />
		</div>
	);
};

const mapStateToProps = (state) => {
	const { leaveLoading, leaveUpdating, leave, leaveTypes, detailedReportDownloading } = state.leave;
	const { enableDepartments, departments, jurisdiction } = state?.company?.settings;

	//All the company's employee
	const employeeListWithSalary = state.employees?.employeeListWithSalary;

	const fullList = _.orderBy(
		employeeListWithSalary?.map((e) => ({
			value: e.id,
			label: toTitleCase(`${e.employeeName?.trim()} ${e.employeeSurname?.trim()}`),
			terminated: e.isTerminated,
			jobType: e.employmentTypeName,
			description: [e.departmentName, e.roleName].filter(Boolean).join(" - "),
			statusMessage: e.terminationDate
				? "Last Working Day: " + format(new Date(e.terminationDate), "dd/MM/yyyy")
				: null,
		})) || [],
		["label", "terminated"],
		["asc", "asc"],
	);

	const employeeOptions = employmentTypeOptions?.map((employmentType) => ({
		label: employmentType.label,
		options: fullList.filter((e) => !e.terminated && e.jobType == employmentType.label),
	}));

	const terminated = fullList.filter((e) => e.terminated);

	employeeOptions.push({
		label: "Terminated",
		options: terminated,
	});

	const leaveTypeOptions = leaveTypes?.map((leaveType) => ({
		value: leaveType.id,
		label: leaveType.name,
	}));

	const leaveStatusOptions = ["Approved", "Pending", "Cancelled", "Denied", "Deleted", "Pending Cancellation"].map(
		(i) => ({ value: i, label: i }),
	);

	const departmentOptions = departments?.map((department) => ({
		value: department.id,
		label: department.name,
	}));

	const sortedLeave = _.orderBy(leave, ["start", "employeeName", "employeeSurname"], ["desc", "asc", "asc"]);

	const jobTypeOptions = employmentTypeOptions?.map((type) => ({
		value: type.label,
		label: type.label,
	}));

	const employeeId = state?.employee?.employeeDetails?.id;

	return {
		leaveLoading,
		leaveUpdating,
		leave: sortedLeave,
		employeeOptions,
		departmentOptions,
		leaveTypeOptions,
		leaveStatusOptions,
		jobTypeOptions,
		detailedReportDownloading,
		hasDepartments: enableDepartments === undefined ? !!departments?.length : enableDepartments,
		jurisdiction,
		userPermissions: state?.permissions?.my?.permissions?.leave,
		employeeId: { value: employeeId },
	};
};

const mapDispatchToProps = (dispatch) => ({
	setOpenModal: (status, leave) => dispatch(LEAVEACTIONS.setOpenModal(status, leave, true)),
	getLeave: (options) => dispatch(LEAVEACTIONS.leaveGet(options, false)),
	downloadDetailedReport: (options) => dispatch(LEAVEACTIONS.downloadDetailedReport(options)),
	getLeaveBalance: () => dispatch(LEAVEACTIONS.getLeaveBalance()),
	getLeaveBalanceReport: () => dispatch(LEAVEACTIONS.getLeaveBalanceReport()),
	saveLeave: (leave) => dispatch(LEAVEACTIONS.saveLeave(leave)),
	showCancelLeave: (leave, status) => dispatch(LEAVEACTIONS.showCancelLeave(leave, status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LeaveTable);
